import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { get } from '@/shared/api/api-client';
import type { QuestionnaireSubmitted } from '@/types/scheduling-api';

/**
 * GET /api/QuestionnaireResponses/ForPatient?appointmentId={id}
 *
 * Mirrors Flutter `SchedulingDataSourceImpl.getFormResponse`
 * (`scheduling_data_source_impl.dart:435-454`). Used by the
 * ViewAppointmentPanel Forms tab (PROD-843) to decide whether to
 * render the read-only submitted view (when `item.length > 0`) or the
 * "awaiting response" placeholder (when `item: []` — verified against
 * the 2026-05-20 dev curl with status `"InProgress"` on an appointment
 * where the patient hadn't filled the form yet).
 *
 * Wire-shape quirks (verbatim, do NOT normalize):
 *   - URL query param `appointmentId` is **lowercase** even though
 *     other Appointment endpoints use PascalCase `AppointmentId`. Match
 *     Flutter (`scheduling_data_source_impl.dart:440`).
 *   - Response top-level array is `item` (singular). Group items
 *     nested under `items` (plural).
 *
 * Forwards `signal` per the project rule (`feedback_signal_forwarding.md`).
 *
 * Cache key includes `appointmentId` so refetch is per-appointment, not
 * global. Enabled only when both `appointmentId` is non-empty AND a
 * questionnaire is attached to the appointment — the consumer passes
 * `enabled: !!appointment.questionnaireForm?.questionnaireId` to skip
 * the network call entirely on appointments without a form.
 */

export interface UseQuestionnaireResponseForPatientParams {
  appointmentId: string | null | undefined;
  enabled?: boolean;
}

export function useQuestionnaireResponseForPatient(
  params: UseQuestionnaireResponseForPatientParams,
): UseQueryResult<QuestionnaireSubmitted> {
  const { appointmentId, enabled = true } = params;

  return useQuery<QuestionnaireSubmitted>({
    queryKey: ['questionnaire-response', appointmentId],
    enabled: enabled && !!appointmentId,
    queryFn: ({ signal }) =>
      get<QuestionnaireSubmitted>(
        `/api/QuestionnaireResponses/ForPatient?appointmentId=${encodeURIComponent(
          appointmentId as string,
        )}`,
        { signal },
      ),
  });
}
